import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { assetUrl } from '../../../utils/assets';
import useIOSKeyboardFix from './useIOSKeyboardFix';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: white;
`;

const ReplyPreviewContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 5px;
  border-left: 3px solid #19bbbb;
  margin: 10px;
  border-radius: 10px;
`;

const ReplyPreviewText = styled.div`
  flex: 1;
  min-width: 0;
  background-color: #f5f5f5;
  padding: 5px;
  border-radius: 10px;
`;

const ReplySenderName = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: #19bbbb;
`;

const ReplyMessageText = styled.div`
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

const CloseReplyBtn = styled.button`
  background-color: #f5f5f5;
  border: none;
  color: #999;
  border-radius: 20px;
  cursor: pointer;
  font-size: 18px;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &:hover {
    color: #333;
  }
`;

const InputBoxContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  gap: 8px;
`;

const PlusButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid #333;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
  margin-right: 8px;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const InputField = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 36px;
  max-height: 120px;
  padding: 10px 16px;
  font-size: 14px;
  border: none;
  border-radius: 20px;
  background-color: #f3f3f6;
  color: #333;
  outline: none;
  line-height: 1.4;

  ::placeholder {
    color: #aaa;
  }

  -webkit-user-select: text;
  -webkit-touch-callout: none;
  font-size: 16px; /* prevent zoom on focus */
`;

const SendButton = styled.button`
  width: 36px;
  height: 36px;
  background-color: #19bbbb;
  border: none;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background-color: #c81618;
  }
`;

const AttachmentPopup = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px 12px 0 0;
  padding: 16px 12px 0;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 20;
`;

const AttachmentHeader = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 12px;
`;

const AttachmentOptionsRow = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
`;

const AttachmentOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;

  &:not(:last-child) {
    border-right: 1px solid #eee;
  }
`;

const AttachmentIcon = styled.img`
  background-color: #ffe4e4;
  border-radius: 50%;
  padding: 10px;
  width: 40px;
  height: 40px;
`;

const Label = styled.span`
  font-size: 12px;
  margin-top: 4px;
`;

type ChatInputProps = {
  onSend: (message: string) => void;
  onSendMedia: (file: File, mediaType: 'image' | 'file' | 'audio' | 'video') => void;
  replyTo?: any;
  onCancelReply?: () => void;
  doctorName?: string;
  isDoctor?: boolean;
};

const ChatInput: React.FC<ChatInputProps> = ({ onSend, onSendMedia,replyTo, onCancelReply, doctorName = 'Dokter', isDoctor = false  }) => {
  const [message, setMessage] = useState('');
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    console.log('replyTo:', replyTo);
  }, [replyTo]);

  const getReplyMessageText = (): string => {
    if (!replyTo) return '';
    
    try {
      const parsedMessage = typeof replyTo.message === 'string' 
        ? JSON.parse(replyTo.message) 
        : replyTo.message;
      
      return parsedMessage.message || replyTo.message;
    } catch (e) {
      return replyTo.message || '';
    }
  };

  const getReplyerName = (): string => {
    if (!replyTo) return '';
    
    if (replyTo.senderName) {
      return replyTo.senderName;
    }
    
    if (replyTo.isDoctor) {
      return doctorName;
    }
    
    return 'Anda';
  };

  const handleSend = () => {
    if (message.trim()) {
      onSend(message);
      setMessage('');
      onCancelReply?.();

      messageInputRef.current?.blur();
      
      if (messageInputRef.current) {
        messageInputRef.current.style.height = 'auto';
      }
    }
  };

  const handleInputResize = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const mediaType = determineMediaType(file);
      onSendMedia(file, mediaType);
      setShowAttachmentMenu(false);
    }
    e.target.value = '';
  };

  const determineMediaType = (file: File): 'image' | 'file' | 'audio' | 'video' => {
    const mime = file.type;
    if (mime.startsWith('image')) return 'image';
    if (mime.startsWith('audio')) return 'audio';
    if (mime.startsWith('video')) return 'video';
    return 'file';
  };

  const toggleAttachmentMenu = () => {
    setShowAttachmentMenu((prev) => !prev);
  };

  useEffect(() => {
    const input = messageInputRef.current;
    if (!input) return;

    // Prevent iOS from auto-scrolling when focusing input
    const handleFocus = () => {
      setTimeout(() => {
        input.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 300);
    };

    input.addEventListener('focus', handleFocus);

    return () => input.removeEventListener('focus', handleFocus);
  }, []);

  return (
    <InputContainer ref={containerRef}>
      {replyTo && (
        <ReplyPreviewContainer>
          <ReplyPreviewText>
            <ReplySenderName>
              {getReplyerName()}
            </ReplySenderName>
            <ReplyMessageText>{getReplyMessageText()}</ReplyMessageText>
          </ReplyPreviewText>
          <CloseReplyBtn onClick={onCancelReply}>
            <FaTimes />
          </CloseReplyBtn>
        </ReplyPreviewContainer>
      )}
      {showAttachmentMenu && (
        <AttachmentPopup>
          <AttachmentHeader>Lampiran</AttachmentHeader>
          <AttachmentOptionsRow>
            <AttachmentOption onClick={() => cameraInputRef.current?.click()}>
              <AttachmentIcon src={assetUrl("icons/camera-icon.svg")} alt="Kamera" />
              <Label>Kamera</Label>
            </AttachmentOption>
            <AttachmentOption onClick={() => galleryInputRef.current?.click()}>
              <AttachmentIcon src={assetUrl("icons/gallery-icon.svg")} alt="Gallery" />
              <Label>Gallery</Label>
            </AttachmentOption>
            <AttachmentOption onClick={() => documentInputRef.current?.click()}>
              <AttachmentIcon src={assetUrl("icons/doc-icon.svg")} alt="Dokumen" />
              <Label>Dokumen</Label>
            </AttachmentOption>
          </AttachmentOptionsRow>
        </AttachmentPopup>
      )}

      <InputBoxContainer>
        {showAttachmentMenu && (
          <AttachmentPopup>
            <AttachmentHeader>Lampiran</AttachmentHeader>
            <AttachmentOptionsRow>
              <AttachmentOption onClick={() => cameraInputRef.current?.click()}>
                <AttachmentIcon src={assetUrl("icons/camera-icon.svg")} alt="Kamera" />
                <Label>Kamera</Label>
              </AttachmentOption>
              <AttachmentOption onClick={() => galleryInputRef.current?.click()}>
                <AttachmentIcon src={assetUrl("icons/gallery-icon.svg")} alt="Gallery" />
                <Label>Gallery</Label>
              </AttachmentOption>
              <AttachmentOption onClick={() => documentInputRef.current?.click()}>
                <AttachmentIcon src={assetUrl("icons/doc-icon.svg")} alt="Dokumen" />
                <Label>Dokumen</Label>
              </AttachmentOption>
            </AttachmentOptionsRow>
          </AttachmentPopup>
        )}

        {!replyTo && (
          <PlusButton onClick={toggleAttachmentMenu}>
            {showAttachmentMenu ? <FaTimes /> : <FaPlus />}
          </PlusButton>
        )}

        <InputField
          ref={messageInputRef}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            handleInputResize(e);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Ketik pesan Anda di sini"
          rows={1}
        />

        <SendButton onClick={handleSend}>
          <img src={assetUrl("icons/send-button.svg")} alt="Kirim" />
        </SendButton>

        {/* Hidden file inputs */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleMediaChange}
        />

        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*,video/*"
          style={{ display: 'none' }}
          onChange={handleMediaChange}
        />

        <input
          ref={documentInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.txt,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style={{ display: 'none' }}
          onChange={handleMediaChange}
        />
      </InputBoxContainer>
    </InputContainer>
  );
};

export default ChatInput;
